import { useState } from "react";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Button from "../UI/Button";
import Text from "../UI/Text";
import Callout from "../UI/Callout";
import Avatar from "../UI/Avatar";
import Status from "../UI/Status";
import { IconCheck, IconX } from "@tabler/icons-react";
// @ts-expect-error pure js
import useLoader from "../../hooks/use-loader";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { ProfileInviteStatus } from "../../models/profile";
import type { Profile } from "./types";
import {
  getDisplayName,
  getInitials,
  isProfileInvitable,
} from "./profileUtils";
import styles from "./InviteLocataireModal.module.css";

interface InviteLocataireModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Titulaires concernés. Avec plusieurs profils, une liste à cocher est
   * proposée ; avec un seul, l'invitation lui est envoyée directement.
   */
  profiles: Profile[];
  onInvite: (profileIds: number[]) => void | Promise<void>;
  resend?: boolean;
}

const BENEFITS = [
  {
    ok: true,
    text: "pourra accéder à tous les documents indiqués comme « partagé avec le locataire »",
  },
  {
    ok: true,
    text: "pourra accéder à ses quittances de loyer, contrat de location, état des lieux, etc.",
  },
  { ok: true, text: "aura accès à l'historique des paiements" },
  {
    ok: true,
    text: "pourra vous déposer des documents depuis son espace (ex : attestation d'assurance)",
  },
  { ok: false, text: "ne pourra pas payer directement depuis son espace" },
];

function inviteStatusText(profile: Profile): string | null {
  if (profile.status_invite === ProfileInviteStatus.CONNECTED) {
    return "Connecté";
  }
  if (profile.status_invite === ProfileInviteStatus.INVITED) {
    return "Déjà invité";
  }
  if (!profile.mail) {
    return "Adresse email manquante";
  }
  return null;
}

export default function InviteLocataireModal({
  open,
  onOpenChange,
  profiles,
  onInvite,
  resend,
}: InviteLocataireModalProps) {
  const showSelection = profiles.length > 1;
  const invitableIds = showSelection
    ? profiles.filter(isProfileInvitable).map((p) => p.id as number)
    : profiles.map((p) => p.id as number);
  // Tous les profils invitables sont cochés par défaut : on ne mémorise que
  // les décochés, ce qui reste juste même si les profils arrivent après le
  // premier rendu.
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  const selectedIds = invitableIds.filter((id) => !excludedIds.has(id));
  const [sentMails, setSentMails] = useState<string[] | null>(null);
  const [error, setError] = useState("");

  const [isSending, handleSend] = useLoader(async () => {
    setError("");
    try {
      await onInvite(selectedIds);
    } catch (err) {
      setError(handleAPIError(err));
      return;
    }
    setSentMails(
      profiles
        .filter((p) => selectedIds.includes(p.id as number))
        .map((p) => p.mail as string),
    );
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setError("");
      setExcludedIds(new Set());
      setSentMails(null);
    }
    onOpenChange(nextOpen);
  }

  function toggle(profileId: number) {
    setExcludedIds((current) => {
      const next = new Set(current);
      if (next.has(profileId)) {
        next.delete(profileId);
      } else {
        next.add(profileId);
      }
      return next;
    });
  }

  const isSent = sentMails !== null;
  const plural = selectedIds.length > 1;
  let header = "Inviter le locataire";
  if (isSent) {
    header = plural ? "Invitations envoyées" : "Invitation envoyée";
  } else if (resend) {
    header = "Renvoyer l'invitation";
  } else if (showSelection) {
    header = "Inviter les locataires";
  }

  return (
    <ModalDialog
      open={open}
      onOpenChange={handleOpenChange}
      header={header}
      size="md"
    >
      <ModalDialogBody className={styles.body}>
        {isSent ? (
          <div className={styles.sent}>
            <Text>
              {plural
                ? "Vos invitations à créer un espace locataire ont bien été envoyées à"
                : "Votre invitation à créer un espace locataire a bien été envoyée à"}
            </Text>
            <Text weight="bold" color="primary">
              {sentMails.join(", ")}
            </Text>
            <Text>
              Dès que le locataire aura accepté l'invitation et créé son compte
              Qalimo, vous en serez informé.
            </Text>
          </div>
        ) : (
          <>
            <div className={styles.explanation}>
              <Text weight="bold" as="h3">
                Comment ça marche ?
              </Text>
              <Text>
                En invitant le locataire, il pourra se créer un compte{" "}
                <Text as="span" weight="bold">
                  gratuitement
                </Text>{" "}
                sur Qalimo et :
              </Text>
              <ul className={styles.benefits}>
                {BENEFITS.map((benefit) => (
                  <li key={benefit.text} className={styles.benefit}>
                    {benefit.ok ? (
                      <IconCheck size={18} className={styles.iconOk} />
                    ) : (
                      <IconX size={18} className={styles.iconKo} />
                    )}
                    <Text as="span">{benefit.text}</Text>
                  </li>
                ))}
              </ul>
            </div>
            {showSelection ? (
              <div className={styles.list}>
                {profiles.map((profile) => {
                  const profileId = profile.id as number;
                  const invitable = isProfileInvitable(profile);
                  const statusText = inviteStatusText(profile);
                  return (
                    <label key={profileId} className={styles.row}>
                      <input
                        type="checkbox"
                        checked={invitable && selectedIds.includes(profileId)}
                        disabled={!invitable}
                        onChange={() => toggle(profileId)}
                      />
                      <Avatar initials={getInitials(profile)} size="md" />
                      <div className={styles.identity}>
                        <Text weight="bold">{getDisplayName(profile)}</Text>
                        {profile.mail && (
                          <Text variant="deci">{profile.mail}</Text>
                        )}
                      </div>
                      {statusText && (
                        <Status
                          color={invitable ? "neutral" : "muted"}
                          text={statusText}
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            ) : (
              profiles[0]?.mail && (
                <Text>
                  L'invitation sera envoyée à{" "}
                  <Text as="span" weight="bold">
                    {profiles[0].mail}
                  </Text>
                  .
                </Text>
              )
            )}
            <Callout variant="danger">{error}</Callout>
          </>
        )}
      </ModalDialogBody>
      <ModalDialogFooter>
        {isSent ? (
          <Button variant="primary" onClick={() => handleOpenChange(false)}>
            Fermer
          </Button>
        ) : (
          <Button
            variant="primary"
            isLoading={isSending}
            disabled={selectedIds.length === 0}
            onClick={() => {
              handleSend().catch(console.error);
            }}
          >
            {resend
              ? "Renvoyer l'invitation"
              : plural
              ? "Envoyer les invitations"
              : "Envoyer l'invitation"}
          </Button>
        )}
      </ModalDialogFooter>
    </ModalDialog>
  );
}
